import { useEffect } from "react";
import MaterialTable from "material-table";
import { Box, Chip, Grid, Paper, Typography, Skeleton, Tooltip } from "@mui/material";
import AssignmentIcon from '@mui/icons-material/Assignment';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import BlockIcon from '@mui/icons-material/Block';
import { useDispatch, useSelector } from "react-redux";
import Base from "../../../Base/views/Base";
import { GetMprDashboardAction, GetMprDashboardCardsAction } from "../../../../../store/ATS/MprRequest/Action";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { MprStatusRenderer } from "./MprStatusRenderer";

const DashboardTab = () => {
    const dispatch = useDispatch();
    const { mpr_dashboard, isLoadingMprDashboard, mpr_dashboard_cards, isLoadingMprDashboardCards } = useSelector((state) => state?.AtsReducer?.MprRequestReducer || {});

    useEffect(() => {
        dispatch(GetMprDashboardAction());
        dispatch(GetMprDashboardCardsAction());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <Base>
            <Box sx={{ height: "calc(100vh - 115px)", overflowY: "auto", mt: 1 }}>
                <Grid container spacing={1} sx={{ mb: 1.5 }}>
                    <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                            {isLoadingMprDashboardCards ? (
                                <>
                                    <Box>
                                        <Skeleton variant="text" width={120} height={18} sx={{ mb: 1 }} />
                                        <Skeleton variant="text" width={40} height={30} />
                                    </Box>
                                    <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: 2 }} />
                                </>
                            ) : (
                                <>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: 14, color: "primary.contrastText", mb: 1 }}>Total Active MPRs</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{mpr_dashboard_cards?.[0]?.total_active_mprs || 0}</Typography>
                                    </Box>
                                    <Box sx={{ width: 48, height: 48, borderRadius: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#e3f2fd' }}>
                                        <AssignmentIcon sx={{ color: '#1976d2' }} />
                                    </Box>
                                </>
                            )}
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                            {isLoadingMprDashboardCards ? (
                                <>
                                    <Box>
                                        <Skeleton variant="text" width={120} height={18} sx={{ mb: 1 }} />
                                        <Skeleton variant="text" width={40} height={30} />
                                    </Box>
                                    <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: 2 }} />
                                </>
                            ) : (
                                <>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: 14, color: "primary.contrastText", mb: 1 }}>Total Positions Open</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{mpr_dashboard_cards?.[0]?.total_positions_open || 0}</Typography>
                                    </Box>
                                    <Box sx={{ width: 48, height: 48, borderRadius: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#e8f5e9' }}>
                                        <WorkOutlineIcon sx={{ color: '#388e3c' }} />
                                    </Box>
                                </>
                            )}
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                            {isLoadingMprDashboardCards ? (
                                <>
                                    <Box>
                                        <Skeleton variant="text" width={120} height={18} sx={{ mb: 1 }} />
                                        <Skeleton variant="text" width={40} height={30} />
                                    </Box>
                                    <Skeleton variant="rounded" width={48} height={48} sx={{ borderRadius: 2 }} />
                                </>
                            ) : (
                                <>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: 14, color: "primary.contrastText", mb: 1 }}>Total Exhausted MPRs</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{mpr_dashboard_cards?.[0]?.total_exhausted_mprs || 0}</Typography>
                                    </Box>
                                    <Box sx={{ width: 48, height: 48, borderRadius: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#fce4ec' }}>
                                        <BlockIcon sx={{ color: '#d32f2f' }} />
                                    </Box>
                                </>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ '& .MuiToolbar-root': { minHeight: '50px !important', height: '50px' } }}>
                    <MaterialTable
                        isLoading={isLoadingMprDashboard}
                        columns={[
                            {
                                title: "Designation",
                                field: "role",
                            },
                            {
                                title: "Department",
                                field: "dept",
                            },
                            {
                                title: "Approved Headcount",
                                field: "headcount",
                                align: "center",
                                render: rowData => rowData?.headcount || "-"
                            },
                            {   title: "Headcount Filled", 
                                field: "filled", 
                                align: "center",
                                render: rowData => rowData?.filled || "-" 
                            },
                            {
                                title: "Approved Budget",
                                field: "budget",
                                align: "right",
                                render: (rowData) => rowData?.is_budgeted ? rowData?.budget || "-" : "N/A",
                            },
                            {
                                title: "Budget Consumed",
                                field: "used",
                                align: "right",
                                render: (rowData) => rowData?.is_budgeted ? rowData?.used || "-" : "N/A",
                            },
                            {
                                title: "Budget Remaining",
                                field: "remaining",
                                align: "right",
                                render: (rowData) => rowData?.is_budgeted ? rowData?.remaining || "-" : "N/A",
                            },
                            {
                                title: (<span>
                                    Budget Utilisation&nbsp;
                                    <Tooltip title="Budget utilization status: Red (>90%), Amber (70–90%), Green (<70%)" placement="bottom" arrow>
                                        <InfoOutlinedIcon color="action" style={{ verticalAlign: "middle", cursor: "pointer", marginBottom: "4px", fontSize: "16px" }} />
                                    </Tooltip>
                                </span>),
                                field: "rag",
                                sorting: false,
                                align: "center",
                                render: (rowData) => {
                                    const { bg, text } = getStatusColor(rowData?.rag);
                                    return (
                                        <Chip
                                            label={rowData.rag}
                                            style={{
                                                backgroundColor: bg,
                                                color: text,
                                                fontWeight: 500,
                                            }}
                                            size="small"
                                        />
                                    );
                                },
                            },
                            {
                                title: "Status",
                                field: "status",
                                sorting: false,
                                align: "center",
                                render: (rowData) => <MprStatusRenderer rowData={{...rowData, mpr_status: rowData?.status}} />
                            },
                        ]}
                        data={mpr_dashboard || []}
                        options={{
                            maxBodyHeight: "calc(100vh - 320px)",
                            minBodyHeight: "calc(100vh - 320px)",
                            rowStyle: {
                                fontSize: "13px",
                                height: "45px",
                            },
                            headerStyle: {
                                padding: "7px 5px 7px 10px",
                                fontWeight: "bold",
                            },
                            cellStyle: {
                                padding: "5px 5px 5px 13px",
                            },
                            sorting: true,
                            search: true,
                            searchFieldAlignment: "right",
                            searchAutoFocus: false,
                            filtering: false,
                            paging: true,
                            pageSizeOptions: [25, 50],
                            pageSize: 25,
                            selection: false,
                            actionsColumnIndex: -1,
                            grouping: false,
                            columnsButton: false,
                            showTitle: false,
                            searchFieldStyle: {
                                height: "35px",
                                marginTop: "0px",
                            },
                            exportAllData: false,
                            exportButton: false,
                        }}
                        title=""
                    />
                </Box>
            </Box>
        </Base>
    );
};

const getStatusColor = (status) => {
    switch (status) {
        case "Pending Approval":
        case "Amber":
            return { bg: "#FFF4E5", text: "#FFA726" }; // yellow
        case "Approved":
        case "Green":
            return { bg: "#E8F5E9", text: "#66BB6A" }; // green
        case "Rejected":
        case "Cancelled":
        case "Red":
            return { bg: "#FFEBEE", text: "#EF5350" }; // red
        case "Submitted":
            return { bg: "#E3F2FD", text: "#42A5F5" };
        default:
            return { bg: "#E5E7EB", text: "#374151" }; // gray
    }
};

export default DashboardTab;

